import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

import { SessionContext } from "../../../../contexts";
import {
  BUILTIN_ENCOUNTER_TYPES,
  TEncounterType,
  SummaryPromptV1,
} from "../../../../utils/types";
import {
  deleteSummaryTemplate,
  getSummaryTemplate,
  updateSummaryTemplate,
  listSummaryTemplateDefaults,
} from "../../../../services/api-documentation";
import {
  listCustomEncounterTypes,
  createCustomEncounterType,
  TCustomEncounterType,
} from "../../../../services/api-settings";
import { Tooltip } from "../../../common/native";

/**
 * ------------------------------
 * Local UI types
 * ------------------------------
 */
type SummarySection = {
  title: string;
  details: string;
};

type ParsedTemplate = {
  instructions: string;
  sections: SummarySection[];
  omitUnsupportedContent: boolean;
};

type SummaryTemplateEntry = {
  instructions: string; // free text; lines starting with "- " become bullets
  sections: SummarySection[]; // UI rows; "details" can include bullets and body text
  systemPrompt: string; // system.role in SummaryPromptV1
  omitUnsupportedContent: boolean;
  templateSource: string | null;
  templateId: number | null; // <— add this
  hasServerValue: boolean;
  isDirty: boolean;
};

const createDefaultEntry = (): SummaryTemplateEntry => ({
  instructions: "",
  sections: [],
  systemPrompt: "",
  omitUnsupportedContent: true,
  templateSource: null,
  templateId: null, // <— add this
  hasServerValue: false,
  isDirty: false,
});

/**
 * ------------------------------
 * Structured <-> UI helpers
 * ------------------------------
 */

const isStructuredV1 = (t: any): t is SummaryPromptV1 =>
  t && typeof t === "object" && t.kind === "summary_prompt/v1";

// Add this type near the top with other types
type LegacySystemUserTemplate = {
  system_prompt?: string;
  user_prompt?: string;
};

// Update the type guard
const isLegacySystemUser = (t: any): t is LegacySystemUserTemplate =>
  t &&
  typeof t === "object" &&
  !isStructuredV1(t) &&
  ("system_prompt" in t || "user_prompt" in t);

// Convert structured instructions into UI text: intro + "- bullets"
const flattenInstructions = (ins?: {
  intro?: string;
  bullets?: string[];
}): string => {
  if (!ins) return "";
  const parts: string[] = [];
  if (ins.intro?.trim()) parts.push(ins.intro.trim());
  if (ins.bullets?.length) parts.push(...ins.bullets.map((b) => `- ${b}`));
  return parts.join("\n");
};

// Convert UI text (with "- bullets") into structured instructions
const expandInstructions = (
  instructionsText: string,
): { intro?: string; bullets?: string[] } => {
  const lines = (instructionsText || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return {};
  const bullets = lines
    .filter((l) => l.startsWith("- "))
    .map((l) => l.slice(2).trim());
  const introLines = lines.filter((l) => !l.startsWith("- "));
  return {
    intro: introLines.join(" ").trim() || undefined,
    bullets: bullets.length ? bullets : undefined,
  };
};

type UIRow = { title: string; details: string };

// Structured -> UI sections: merge body + "- bullets" into a single details textarea
const toUISections = (
  sections?: { title: string; bullets?: string[]; body?: string }[],
): UIRow[] =>
  (sections ?? []).map((s) => ({
    title: s.title || "",
    details: [s.body?.trim() ?? "", ...(s.bullets?.map((b) => `- ${b}`) ?? [])]
      .filter(Boolean)
      .join("\n"),
  }));

// UI -> Structured sections
const toStructuredSections = (
  rows: UIRow[],
): { title: string; bullets?: string[]; body?: string }[] =>
  rows
    .map(({ title, details }) => {
      const t = (title || "").trim();
      if (!t) return null;
      const lines = (details || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      const bullets = lines
        .filter((l) => l.startsWith("- "))
        .map((l) => l.slice(2).trim());
      const bodyLines = lines.filter((l) => !l.startsWith("- "));
      return {
        title: t,
        body: bodyLines.length ? bodyLines.join("\n") : undefined,
        bullets: bullets.length ? bullets : undefined,
      };
    })
    .filter(Boolean) as any[];

// Build SummaryPromptV1 from the UI store
const composeStructuredTemplateV1 = (opts: {
  encounterType: string;
  label?: string;
  systemPrompt?: string;
  omitUnsupportedContent: boolean;
  instructionsText: string;
  sections: { title: string; details: string }[];
}): SummaryPromptV1 => ({
  kind: "summary_prompt/v1",
  meta: {
    label: opts.label || `${opts.encounterType} — Custom`,
    encounter_type: opts.encounterType,
  },
  system: {
    role: (
      opts.systemPrompt || "You are an expert medical documentation assistant."
    ).trim(),
  },
  header: {
    lines: [
      "Encounter Date: {{date}}",
      "Patient: {{patient_name}}",
      "Encounter Type: {{encounter_label}}",
    ],
  },
  instructions: expandInstructions(opts.instructionsText),
  sections: toStructuredSections(opts.sections),
  transcript_policy: {
    position: "append",
    label: "FULL TRANSCRIPT",
    placeholder: "<<<transcription>>>",
  },
  output_policy: {
    omit_unsupported_content: opts.omitUnsupportedContent,
  },
});

/**
 * ------------------------------
 * Store types & context
 * ------------------------------
 */

type ConsultSettingsStore = {
  encounterTypes: TEncounterType[];
  loadingEncounterTypes: boolean;
  selectedEncounterType: TEncounterType | null;
  selectEncounterType: (encounterType: TEncounterType) => void;
  template: SummaryTemplateEntry | null;
  updateInstructions: (value: string) => void;
  updateOmissionPolicy: (omitUnsupportedContent: boolean) => void;
  updateSectionTitle: (index: number, value: string) => void;
  updateSectionDetails: (index: number, value: string) => void;
  addRequiredSection: () => void;
  removeRequiredSection: (index: number) => void;
  saveTemplate: () => Promise<void>;
  resetTemplate: () => Promise<void>;
  resetToDefault: () => void;
  reloadTemplate: () => void;
  loadingTemplate: boolean;
  savingTemplate: boolean;
  deletingTemplate: boolean;
  error: string | null;
  hasChanges: boolean;
  defaultTemplate: ParsedTemplate | null;
  loadingDefaults: boolean;
  createEncounterType: (name: string) => Promise<void>;
  creatingEncounterType: boolean;
  encounterTypeCreationError: string | null;
};

const ConsultSettingsContext = createContext<ConsultSettingsStore | null>(null);

/**
 * ------------------------------
 * Helpers
 * ------------------------------
 */

const uniqEncounterTypes = (
  custom: TCustomEncounterType[],
): TEncounterType[] => {
  const builtin = Array.from(BUILTIN_ENCOUNTER_TYPES);
  const customNames = custom
    .map((item) => item.name)
    .filter((name): name is string => Boolean(name))
    .sort((a, b) => a.localeCompare(b));

  return Array.from(new Set<TEncounterType>([...builtin, ...customNames]));
};

/**
 * ------------------------------
 * Hook: useProvideConsultSettings
 * ------------------------------
 */
const useProvideConsultSettings = (): ConsultSettingsStore => {
  const session = useContext(SessionContext)?.session ?? null;

  const [encounterTypes, setEncounterTypes] = useState<TEncounterType[]>(() =>
    Array.from(BUILTIN_ENCOUNTER_TYPES),
  );
  const [selectedEncounterType, setSelectedEncounterType] =
    useState<TEncounterType | null>(() => BUILTIN_ENCOUNTER_TYPES[0] ?? null);
  const [customEncounterTypes, setCustomEncounterTypes] = useState<
    TCustomEncounterType[]
  >([]);
  const [creatingEncounterType, setCreatingEncounterType] = useState(false);
  const [encounterTypeCreationError, setEncounterTypeCreationError] = useState<
    string | null
  >(null);

  const [templatesByEncounter, setTemplatesByEncounter] = useState<
    Record<string, SummaryTemplateEntry>
  >({});

  const [loadingEncounterTypes, setLoadingEncounterTypes] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [defaultTemplates, setDefaultTemplates] = useState<
    Record<string, ParsedTemplate>
  >({});
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  const currentTemplate = useMemo(() => {
    if (!selectedEncounterType) return null;
    return templatesByEncounter[selectedEncounterType] ?? createDefaultEntry();
  }, [selectedEncounterType, templatesByEncounter]);

  const defaultTemplate = useMemo(() => {
    if (!selectedEncounterType) return null;
    return defaultTemplates[selectedEncounterType] ?? null;
  }, [defaultTemplates, selectedEncounterType]);

  const selectEncounterType = useCallback((encounterType: TEncounterType) => {
    setSelectedEncounterType(encounterType);
    setError(null);
  }, []);

  useEffect(() => {
    let isActive = true;

    const hydrateEncounterTypes = async () => {
      if (!session?.clinicId) return;

      setLoadingEncounterTypes(true);
      setError(null);

      try {
        const custom = await listCustomEncounterTypes(
          session,
          session.clinicId,
        );
        if (!isActive) return;

        setCustomEncounterTypes(custom);

        const merged = uniqEncounterTypes(custom);
        setEncounterTypes(merged);

        setSelectedEncounterType((prev) => {
          if (prev && merged.includes(prev)) return prev;
          return merged[0] ?? null;
        });
      } catch (err) {
        if (!isActive) return;
        const message =
          err instanceof Error ? err.message : "Failed to load encounter types";
        setError(message);
        setCustomEncounterTypes([]);
        setEncounterTypes(Array.from(BUILTIN_ENCOUNTER_TYPES));
      } finally {
        if (isActive) setLoadingEncounterTypes(false);
      }
    };

    hydrateEncounterTypes();
    return () => {
      isActive = false;
    };
  }, [session]);

  useEffect(() => {
    let isActive = true;

    const hydrateDefaults = async () => {
      if (!session?.clinicId) return;

      setLoadingDefaults(true);
      try {
        const templates = await listSummaryTemplateDefaults(session);
        if (!isActive) return;

        const mapped = templates.reduce<Record<string, ParsedTemplate>>(
          (acc, t) => {
            const tpl = t.template;
            if (tpl && isStructuredV1(tpl)) {
              acc[t.encounterType] = {
                instructions: flattenInstructions(tpl.instructions),
                sections: toUISections(tpl.sections).map(
                  ({ title, details }) => ({
                    title,
                    details,
                  }),
                ),
                omitUnsupportedContent:
                  tpl.output_policy?.omit_unsupported_content ?? true,
              };
            } else if (typeof tpl === "string") {
              acc[t.encounterType] = {
                instructions: tpl,
                sections: [],
                omitUnsupportedContent: true,
              };
            }
            return acc;
          },
          {},
        );

        setDefaultTemplates(mapped);
      } catch (err) {
        if (!isActive) return;
        const message =
          err instanceof Error
            ? err.message
            : "Failed to load default templates";
        setError(message);
      } finally {
        if (isActive) setLoadingDefaults(false);
      }
    };

    hydrateDefaults();
    return () => {
      isActive = false;
    };
  }, [session]);

  useEffect(() => {
    let isActive = true;

    const loadTemplate = async (encounterType: TEncounterType) => {
      if (!session?.clinicId) return;
      if (templatesByEncounter[encounterType]) return;

      setLoadingTemplate(true);
      setError(null);

      try {
        const response = await getSummaryTemplate(session, {
          encounterType,
        });
        if (!isActive) return;

        const td = response.templateData;

        if (td && isStructuredV1(td)) {
          setTemplatesByEncounter((prev) => ({
            ...prev,
            [encounterType]: {
              instructions: flattenInstructions(td.instructions),
              sections: toUISections(td.sections).map(({ title, details }) => ({
                title,
                details,
              })),
              systemPrompt: td.system?.role ?? "",
              omitUnsupportedContent:
                td.output_policy?.omit_unsupported_content ?? true,
              templateId: response.templateId ?? null,
              templateSource: response.templateSource,
              hasServerValue: true,
              isDirty: false,
            },
          }));
        } else if (td && isLegacySystemUser(td)) {
          // Cast td to the legacy type since type guard isn't working
          const legacyTd = td as LegacySystemUserTemplate;
          setTemplatesByEncounter((prev) => ({
            ...prev,
            [encounterType]: {
              instructions: legacyTd.user_prompt ?? "",
              sections: [],
              systemPrompt: legacyTd.system_prompt ?? "",
              omitUnsupportedContent: true,
              templateId: response.templateId ?? null,
              templateSource: response.templateSource,
              hasServerValue: true,
              isDirty: false,
            },
          }));
        } else if (!td && response.userPrompt) {
          setTemplatesByEncounter((prev) => ({
            ...prev,
            [encounterType]: {
              ...createDefaultEntry(),
              instructions: response.userPrompt ?? "",
              templateId: null,
              templateSource: response.templateSource,
              hasServerValue: false,
              isDirty: false,
            },
          }));
        } else {
          setTemplatesByEncounter((prev) => ({
            ...prev,
            [encounterType]: createDefaultEntry(),
          }));
        }
      } catch (err) {
        if (!isActive) return;
        const message =
          err instanceof Error ? err.message : "Failed to load template";
        setError(message);
      } finally {
        if (isActive) setLoadingTemplate(false);
      }
    };

    if (selectedEncounterType) loadTemplate(selectedEncounterType);
    return () => {
      isActive = false;
    };
  }, [session, selectedEncounterType, templatesByEncounter]);

  const updateTemplateEntry = useCallback(
    (
      encounterType: TEncounterType,
      updater: (entry: SummaryTemplateEntry) => SummaryTemplateEntry,
    ) => {
      setTemplatesByEncounter((prev) => {
        const current = prev[encounterType] ?? createDefaultEntry();
        const next = updater(current);
        return { ...prev, [encounterType]: next };
      });
    },
    [],
  );

  const updateInstructions = useCallback(
    (value: string) => {
      if (!selectedEncounterType) return;
      updateTemplateEntry(selectedEncounterType, (entry) => ({
        ...entry,
        instructions: value,
        isDirty: true,
      }));
    },
    [selectedEncounterType, updateTemplateEntry],
  );

  const updateOmissionPolicy = useCallback(
    (omitUnsupportedContent: boolean) => {
      if (!selectedEncounterType) return;
      updateTemplateEntry(selectedEncounterType, (entry) => ({
        ...entry,
        omitUnsupportedContent,
        isDirty: true,
      }));
    },
    [selectedEncounterType, updateTemplateEntry],
  );

  const updateSectionTitle = useCallback(
    (index: number, value: string) => {
      if (!selectedEncounterType) return;
      updateTemplateEntry(selectedEncounterType, (entry) => {
        const next = [...entry.sections];
        const existing = next[index] ?? { title: "", details: "" };
        next[index] = { ...existing, title: value };
        return { ...entry, sections: next, isDirty: true };
      });
    },
    [selectedEncounterType, updateTemplateEntry],
  );

  const updateSectionDetails = useCallback(
    (index: number, value: string) => {
      if (!selectedEncounterType) return;
      updateTemplateEntry(selectedEncounterType, (entry) => {
        const next = [...entry.sections];
        const existing = next[index] ?? { title: "", details: "" };
        next[index] = { ...existing, details: value };
        return { ...entry, sections: next, isDirty: true };
      });
    },
    [selectedEncounterType, updateTemplateEntry],
  );

  const addRequiredSection = useCallback(() => {
    if (!selectedEncounterType) return;
    updateTemplateEntry(selectedEncounterType, (entry) => ({
      ...entry,
      sections: [...entry.sections, { title: "", details: "" }],
      isDirty: true,
    }));
  }, [selectedEncounterType, updateTemplateEntry]);

  const removeRequiredSection = useCallback(
    (index: number) => {
      if (!selectedEncounterType) return;
      updateTemplateEntry(selectedEncounterType, (entry) => ({
        ...entry,
        sections: entry.sections.filter((_, i) => i !== index),
        isDirty: true,
      }));
    },
    [selectedEncounterType, updateTemplateEntry],
  );

  const saveTemplate = useCallback(async () => {
    if (!session?.clinicId || !selectedEncounterType || !currentTemplate)
      return;

    setSavingTemplate(true);
    setError(null);

    try {
      const structured = composeStructuredTemplateV1({
        encounterType: selectedEncounterType,
        label: `Summary: ${selectedEncounterType}`,
        systemPrompt: currentTemplate.systemPrompt,
        omitUnsupportedContent: currentTemplate.omitUnsupportedContent,
        instructionsText: currentTemplate.instructions,
        sections: currentTemplate.sections,
      });

      const response = await updateSummaryTemplate(session, {
        templateData: structured,
        encounterType: selectedEncounterType,
        templateName: `summary:${selectedEncounterType}:custom`,
      });

      const td = response.templateData!;
      setTemplatesByEncounter((prev) => ({
        ...prev,
        [selectedEncounterType]: {
          instructions: flattenInstructions(td.instructions),
          sections: toUISections(td.sections).map(({ title, details }) => ({
            title,
            details,
          })),
          systemPrompt: td.system?.role ?? currentTemplate.systemPrompt ?? "",
          omitUnsupportedContent:
            td.output_policy?.omit_unsupported_content ??
            currentTemplate.omitUnsupportedContent,
          templateId: response.templateId ?? currentTemplate.templateId ?? null,
          templateSource: response.templateSource,
          hasServerValue: true,
          isDirty: false,
        },
      }));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save template";
      setError(message);
      throw err;
    } finally {
      setSavingTemplate(false);
    }
  }, [session, selectedEncounterType, currentTemplate]);

  const resetTemplate = useCallback(async () => {
    if (!session?.clinicId || !selectedEncounterType) return;

    setDeletingTemplate(true);
    setError(null);

    try {
      const current = templatesByEncounter[selectedEncounterType];
      await deleteSummaryTemplate(session, {
        encounterType: selectedEncounterType,
        templateId: current?.templateId ?? null, // precise delete when possible
      });

      setTemplatesByEncounter((prev) => ({
        ...prev,
        [selectedEncounterType]: createDefaultEntry(),
      }));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to delete template";
      setError(message);
      throw err;
    } finally {
      setDeletingTemplate(false);
    }
  }, [session, selectedEncounterType, templatesByEncounter]);

  const resetToDefault = useCallback(() => {
    if (!selectedEncounterType) return;

    const selectedDefaults = defaultTemplates[selectedEncounterType];
    const consultationDefaults = defaultTemplates["Consultation"];
    const d = selectedDefaults ?? consultationDefaults ?? null;

    setTemplatesByEncounter((prev) => ({
      ...prev,
      [selectedEncounterType]: {
        ...(prev[selectedEncounterType] ?? createDefaultEntry()),
        instructions: d?.instructions ?? "",
        sections: d?.sections ?? [],
        omitUnsupportedContent: d?.omitUnsupportedContent ?? true,
        isDirty: true,
      },
    }));
  }, [defaultTemplates, selectedEncounterType]);

  const reloadTemplate = useCallback(() => {
    if (!selectedEncounterType) return;
    setTemplatesByEncounter((prev) => {
      const next = { ...prev };
      delete next[selectedEncounterType];
      return next;
    });
  }, [selectedEncounterType]);

  const createEncounterType = useCallback(
    async (name: string) => {
      if (!session?.clinicId) {
        throw new Error("Missing clinic or clinic ID");
      }

      const trimmed = name.trim();
      if (!trimmed) {
        throw new Error("Note type name is required.");
      }

      setEncounterTypeCreationError(null);
      setCreatingEncounterType(true);
      try {
        const created = await createCustomEncounterType(
          session,
          trimmed,
          session.clinicId,
        );
        const cleanName = created?.name?.trim();
        if (!cleanName) {
          throw new Error("Failed to create note type.");
        }

        setCustomEncounterTypes((prev) => {
          const next = [...prev, created];
          setEncounterTypes(uniqEncounterTypes(next));
          return next;
        });
        setSelectedEncounterType(cleanName);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to create note type.";
        setEncounterTypeCreationError(message);
        throw err;
      } finally {
        setCreatingEncounterType(false);
      }
    },
    [session],
  );

  const hasChanges = Boolean(currentTemplate?.isDirty);

  return {
    encounterTypes,
    loadingEncounterTypes,
    selectedEncounterType,
    selectEncounterType,
    template: currentTemplate,
    updateInstructions,
    updateOmissionPolicy,
    updateSectionTitle,
    updateSectionDetails,
    addRequiredSection,
    removeRequiredSection,
    saveTemplate,
    resetTemplate,
    resetToDefault,
    reloadTemplate,
    loadingTemplate,
    savingTemplate,
    deletingTemplate,
    error,
    hasChanges,
    defaultTemplate,
    loadingDefaults,
    createEncounterType,
    creatingEncounterType,
    encounterTypeCreationError,
  };
};

/**
 * ------------------------------
 * Public hooks & provider
 * ------------------------------
 */

export const useConsultSettings = (): ConsultSettingsStore => {
  const context = useContext(ConsultSettingsContext);
  if (!context) {
    throw new Error(
      "useConsultSettings must be used within a ConsultSettingsProvider",
    );
  }
  return context;
};

export const ConsultSettingsProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const value = useProvideConsultSettings();
  return (
    <ConsultSettingsContext.Provider value={value}>
      {children ?? null}
    </ConsultSettingsContext.Provider>
  );
};

/**
 * ------------------------------
 * UI
 * ------------------------------
 */

const ConsultSettingsContent: React.FC = () => {
  const {
    encounterTypes,
    loadingEncounterTypes,
    selectedEncounterType,
    selectEncounterType,
    template,
    updateInstructions,
    updateOmissionPolicy,
    updateSectionTitle,
    updateSectionDetails,
    addRequiredSection,
    removeRequiredSection,
    saveTemplate,
    resetToDefault,
    reloadTemplate,
    loadingTemplate,
    loadingDefaults,
    savingTemplate,
    error,
    hasChanges,
    defaultTemplate,
    createEncounterType,
    creatingEncounterType,
    encounterTypeCreationError,
  } = useConsultSettings();

  const [saveError, setSaveError] = useState<string | null>(null);
  const [showExample, setShowExample] = useState(true);
  const [showAddNoteType, setShowAddNoteType] = useState(false);
  const [newNoteTypeName, setNewNoteTypeName] = useState("");
  const [localAddNoteError, setLocalAddNoteError] = useState<string | null>(
    null,
  );
  const [showSectionVisibilityTooltip, setShowSectionVisibilityTooltip] =
    useState(false);
  const sectionVisibilityTooltipRef = useRef<any>(null);

  const handleSave = useCallback(async () => {
    try {
      setSaveError(null);
      await saveTemplate();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to save template";
      setSaveError(message);
    }
  }, [saveTemplate]);

  const handleCreateNoteType = useCallback(async () => {
    if (!newNoteTypeName.trim()) {
      setLocalAddNoteError("Please enter a name.");
      return;
    }

    try {
      await createEncounterType(newNoteTypeName);
      setNewNoteTypeName("");
      setShowAddNoteType(false);
      setLocalAddNoteError(null);
    } catch (err) {
      setLocalAddNoteError(
        err instanceof Error ? err.message : "Failed to create note type.",
      );
    }
  }, [createEncounterType, newNoteTypeName]);

  const isLoadingTemplate = loadingTemplate && !template;

  return (
    <View style={styles.container}>
      <View style={styles.sidebar}>
        {loadingEncounterTypes ? (
          <ActivityIndicator />
        ) : (
          <ScrollView contentContainerStyle={styles.encounterList}>
            {encounterTypes.map((encounterType) => {
              const isActive = encounterType === selectedEncounterType;
              return (
                <TouchableOpacity
                  key={encounterType}
                  style={[
                    styles.encounterItem,
                    isActive && styles.encounterItemActive,
                  ]}
                  onPress={() => selectEncounterType(encounterType)}
                  accessibilityRole="button"
                  accessibilityLabel={`Select ${encounterType} encounter type`}
                >
                  <Text
                    style={[
                      styles.encounterItemText,
                      isActive && styles.encounterItemTextActive,
                    ]}
                  >
                    {encounterType}
                  </Text>
                </TouchableOpacity>
              );
            })}
            {showAddNoteType ? (
              <View style={styles.addTypeForm}>
                <TextInput
                  value={newNoteTypeName}
                  onChangeText={(value) => {
                    setNewNoteTypeName(value);
                    setLocalAddNoteError(null);
                  }}
                  placeholder="Enter new note type"
                  style={styles.addTypeInput}
                  autoFocus
                />
                <View style={styles.addTypeButtonRow}>
                  <TouchableOpacity
                    style={[
                      styles.addTypeActionButton,
                      creatingEncounterType &&
                        styles.addTypeActionButtonDisabled,
                    ]}
                    onPress={handleCreateNoteType}
                    disabled={creatingEncounterType}
                  >
                    {creatingEncounterType ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Text style={styles.addTypeActionText}>
                        Save note type
                      </Text>
                    )}
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.addTypeCancelButton}
                    onPress={() => {
                      setShowAddNoteType(false);
                      setNewNoteTypeName("");
                      setLocalAddNoteError(null);
                    }}
                  >
                    <Text style={styles.addTypeCancelText}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <TouchableOpacity
                style={styles.addTypeBtn}
                onPress={() => {
                  setShowAddNoteType(true);
                  setLocalAddNoteError(null);
                }}
              >
                <Text style={styles.addTypeText}>Add a new note type</Text>
              </TouchableOpacity>
            )}
            {(localAddNoteError || encounterTypeCreationError) && (
              <Text style={styles.addTypeErrorText}>
                {localAddNoteError || encounterTypeCreationError}
              </Text>
            )}
          </ScrollView>
        )}
      </View>

      <View style={styles.content}>
        {selectedEncounterType === null ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyStateText}>
              Select an encounter type to configure your summary template.
            </Text>
          </View>
        ) : isLoadingTemplate ? (
          <View style={styles.loadingState}>
            <ActivityIndicator size="large" />
          </View>
        ) : (
          <ScrollView
            contentContainerStyle={styles.templateContainer}
            keyboardShouldPersistTaps="handled"
          >
            <Text style={styles.sectionLabel}>Instructions</Text>
            <TextInput
              style={styles.instructionsInput}
              multiline
              placeholder="Provide detailed instructions for this encounter type"
              value={template?.instructions ?? ""}
              onChangeText={updateInstructions}
              textAlignVertical="top"
            />
            <Text style={styles.helperTextMuted}>
              Explain how the AI should summarize the transcript. Include tone,
              level of detail, and any specific rules (e.g., “state ‘Not
              discussed’ when information is missing”). Tip: Lines starting with{" "}
              <Text style={{ fontWeight: "600" }}>- </Text>
              are treated as bullets.
            </Text>

            <Text style={styles.sectionLabel}>Section visibility</Text>
            <View style={styles.sectionHelperRow}>
              <Text style={styles.helperTextMutedInline}>
                Choose whether to show every section or only sections mentioned
                in the consult.
              </Text>
              <View ref={sectionVisibilityTooltipRef}>
                <TouchableOpacity
                  onPress={() => setShowSectionVisibilityTooltip(true)}
                  style={styles.sectionTooltipTrigger}
                  accessibilityRole="button"
                  accessibilityLabel="Show section visibility example"
                >
                  <Text style={styles.sectionTooltipTriggerText}>
                    See example
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.policyButtonRow}>
              <TouchableOpacity
                onPress={() => updateOmissionPolicy(false)}
                style={[
                  styles.policyButton,
                  template?.omitUnsupportedContent === false &&
                    styles.policyButtonActive,
                ]}
                accessibilityRole="button"
                accessibilityLabel="Always include all sections in summary output"
              >
                <Text
                  style={[
                    styles.policyButtonText,
                    template?.omitUnsupportedContent === false &&
                      styles.policyButtonTextActive,
                  ]}
                >
                  Always include all sections
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => updateOmissionPolicy(true)}
                style={[
                  styles.policyButton,
                  template?.omitUnsupportedContent && styles.policyButtonActive,
                ]}
                accessibilityRole="button"
                accessibilityLabel="Only include sections mentioned in the consult"
              >
                <Text
                  style={[
                    styles.policyButtonText,
                    template?.omitUnsupportedContent &&
                      styles.policyButtonTextActive,
                  ]}
                >
                  Only include mentioned sections
                </Text>
              </TouchableOpacity>
            </View>
            {showSectionVisibilityTooltip && (
              <Tooltip
                targetRef={sectionVisibilityTooltipRef}
                text='Example: If "Family History" is not discussed, it will still appear with "Always include all sections," but be hidden with "Only include mentioned sections."'
                onDismiss={() => setShowSectionVisibilityTooltip(false)}
              />
            )}

            <View style={styles.requiredHeaderRow}>
              <Text style={styles.sectionLabel}>Required Sections</Text>
              <TouchableOpacity
                onPress={addRequiredSection}
                style={styles.addSectionButton}
                accessibilityRole="button"
                accessibilityLabel="Add required section"
              >
                <Text style={styles.addSectionButtonText}>Add Section</Text>
              </TouchableOpacity>
            </View>

            {template && template.sections.length === 0 && (
              <Text style={styles.hintText}>
                Add sections to define the headings that should appear in the
                summary. In “Guidance”, you can write a paragraph and/or bullets
                starting with “- ”.
              </Text>
            )}

            {template?.sections.map((sectionValue, index) => {
              const { title, details } = sectionValue;
              return (
                <View key={`${index}`} style={styles.sectionCard}>
                  <View style={styles.sectionRow}>
                    <TextInput
                      style={styles.sectionTitleInput}
                      placeholder="Section title (e.g., Chief Complaint)"
                      value={title}
                      onChangeText={(value) => updateSectionTitle(index, value)}
                    />
                    <TouchableOpacity
                      onPress={() => removeRequiredSection(index)}
                      style={styles.removeSectionButton}
                      accessibilityRole="button"
                      accessibilityLabel={`Remove section ${index + 1}`}
                    >
                      <Text style={styles.removeSectionButtonText}>Remove</Text>
                    </TouchableOpacity>
                  </View>
                  <TextInput
                    style={styles.sectionDetailsInput}
                    multiline
                    placeholder="Guidance for this section. Use free text and/or bullets that start with “- ”"
                    value={details}
                    onChangeText={(value) => updateSectionDetails(index, value)}
                    textAlignVertical="top"
                  />
                </View>
              );
            })}

            <View style={styles.buttonRow}>
              <TouchableOpacity
                onPress={handleSave}
                disabled={!hasChanges || savingTemplate}
                style={[
                  styles.primaryButton,
                  (!hasChanges || savingTemplate) &&
                    styles.primaryButtonDisabled,
                ]}
              >
                {savingTemplate ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.primaryButtonText}>Save</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                onPress={resetToDefault}
                disabled={loadingDefaults || !defaultTemplate}
                style={[
                  styles.secondaryButton,
                  (loadingDefaults || !defaultTemplate) &&
                    styles.secondaryButtonDisabled,
                ]}
              >
                <Text style={styles.secondaryButtonText}>Reset to Default</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={reloadTemplate}
                style={styles.tertiaryButton}
              >
                <Text style={styles.tertiaryButtonText}>Reload</Text>
              </TouchableOpacity>
            </View>

            {error ? <Text style={styles.errorText}>{error}</Text> : null}
            {saveError ? (
              <Text style={styles.errorText}>{saveError}</Text>
            ) : null}

            {defaultTemplate ? (
              <Text style={styles.helperText}>
                Reset loads the default template for this encounter type (or the
                Consultation default if none). Tweak the instructions and
                section guidance, then press Save.
              </Text>
            ) : (
              <Text style={styles.helperTextMuted}>
                No default template is available yet for this encounter type.
                You can build one from scratch using the structure above.
              </Text>
            )}

            <TouchableOpacity
              onPress={() => setShowExample((previous) => !previous)}
              style={styles.exampleToggle}
            >
              <Text style={styles.exampleToggleText}>
                {showExample ? "Hide" : "Show"} example prompt structure
              </Text>
            </TouchableOpacity>

            {showExample ? (
              <View style={styles.exampleContainer}>
                <Text style={styles.exampleHeading}>Instructions Example</Text>
                <Text style={styles.exampleBody}>
                  {defaultTemplate?.instructions?.length
                    ? defaultTemplate.instructions
                    : "Guide the AI on tone, level of detail, and information hierarchy. For example: 'Create a comprehensive consultation note capturing all risks, benefits, and measurements mentioned in the transcript. Use formal clinical language and state “Not mentioned” when information is missing.'"}
                </Text>

                <Text style={[styles.exampleHeading, { marginTop: 16 }]}>
                  Required Sections Example
                </Text>
                <View style={styles.exampleList}>
                  {(defaultTemplate?.sections?.length
                    ? defaultTemplate.sections.map((section) => section.title)
                    : [
                        "Chief Complaint",
                        "History of Present Illness",
                        "Assessment and Plan",
                        "Informed Consent Discussion",
                        "Follow-Up Plan",
                      ]
                  ).map((sectionTitle) => (
                    <Text key={sectionTitle} style={styles.exampleListItem}>
                      • {sectionTitle}
                    </Text>
                  ))}
                </View>
                <Text style={styles.exampleFootnote}>
                  Each required section becomes a heading in the generated
                  summary. Use the title field for the heading (e.g., “Chief
                  Complaint”) and capture supporting bullet points or prompts in
                  the guidance field beneath it (bullets start with “- ”).
                </Text>
              </View>
            ) : null}
          </ScrollView>
        )}
      </View>
    </View>
  );
};

const ConsultSettings: React.FC = () => (
  <ConsultSettingsProvider>
    <ConsultSettingsContent />
  </ConsultSettingsProvider>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
  },
  sidebar: {
    width: 240,
    borderRightWidth: 1,
    borderRightColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
  },
  encounterList: {
    paddingVertical: 12,
  },
  encounterItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  encounterItemActive: {
    backgroundColor: "#E0F2FE",
  },
  encounterItemText: {
    fontSize: 14,
    color: "#1F2937",
  },
  encounterItemTextActive: {
    fontWeight: "600",
    color: "#0C4A6E",
  },
  addTypeBtn: {
    marginTop: 8,
    marginHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#1F2937",
    alignItems: "center",
  },
  addTypeText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#1F2937",
  },
  addTypeForm: {
    marginTop: 12,
    marginHorizontal: 16,
  },
  addTypeInput: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 14,
    color: "#1F2937",
    backgroundColor: "#FFFFFF",
  },
  addTypeButtonRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
  },
  addTypeActionButton: {
    flex: 1,
    backgroundColor: "#2563EB",
    borderRadius: 6,
    paddingVertical: 10,
    alignItems: "center",
  },
  addTypeActionButtonDisabled: {
    opacity: 0.6,
  },
  addTypeActionText: {
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: 13,
  },
  addTypeCancelButton: {
    marginLeft: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  addTypeCancelText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#374151",
  },
  addTypeErrorText: {
    marginTop: 4,
    marginHorizontal: 16,
    color: "#DC2626",
    fontSize: 12,
  },
  content: {
    flex: 1,
  },
  emptyState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  emptyStateText: {
    fontSize: 16,
    color: "#6B7280",
    textAlign: "center",
  },
  loadingState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  templateContainer: {
    padding: 24,
    gap: 16,
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#111827",
  },
  sectionHelperRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 16,
  },
  sectionTooltipTrigger: {
    paddingVertical: 2,
    paddingHorizontal: 2,
  },
  sectionTooltipTriggerText: {
    fontSize: 12,
    fontWeight: "500",
    color: "#2563EB",
  },
  helperTextMutedInline: {
    color: "#6B7280",
    fontSize: 13,
  },
  instructionsInput: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    padding: 12,
    minHeight: 160,
    fontSize: 14,
    color: "#1F2937",
    backgroundColor: "#FFFFFF",
  },
  requiredHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  policyButtonRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  policyButton: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "#FFFFFF",
  },
  policyButtonActive: {
    borderColor: "#2563EB",
    backgroundColor: "#EFF6FF",
  },
  policyButtonText: {
    fontSize: 13,
    color: "#374151",
    fontWeight: "500",
  },
  policyButtonTextActive: {
    color: "#1D4ED8",
    fontWeight: "600",
  },
  addSectionButton: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#2563EB",
  },
  addSectionButtonText: {
    color: "#2563EB",
    fontWeight: "500",
    fontSize: 13,
  },
  hintText: {
    fontSize: 13,
    color: "#6B7280",
  },
  sectionCard: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    padding: 12,
    backgroundColor: "#FFFFFF",
    gap: 8,
  },
  sectionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  sectionTitleInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: "#FFFFFF",
    color: "#1F2937",
  },
  removeSectionButton: {
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  removeSectionButtonText: {
    color: "#DC2626",
    fontWeight: "500",
    fontSize: 13,
  },
  sectionDetailsInput: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 10,
    minHeight: 96,
    fontSize: 13,
    lineHeight: 18,
    backgroundColor: "#FFFFFF",
    color: "#1F2937",
  },
  buttonRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginTop: 12,
  },
  primaryButton: {
    backgroundColor: "#2563EB",
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 18,
  },
  primaryButtonDisabled: {
    backgroundColor: "#93C5FD",
  },
  primaryButtonText: {
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: 14,
  },
  secondaryButton: {
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: "#9CA3AF",
  },
  secondaryButtonDisabled: {
    borderColor: "#D1D5DB",
  },
  secondaryButtonText: {
    color: "#374151",
    fontWeight: "500",
    fontSize: 14,
  },
  tertiaryButton: {
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  tertiaryButtonText: {
    color: "#2563EB",
    fontWeight: "500",
    fontSize: 14,
  },
  errorText: {
    marginTop: 12,
    color: "#DC2626",
    fontSize: 13,
  },
  helperText: {
    marginTop: 16,
    color: "#374151",
    fontSize: 13,
  },
  helperTextMuted: {
    marginTop: 16,
    color: "#6B7280",
    fontSize: 13,
  },
  exampleToggle: {
    marginTop: 20,
  },
  exampleToggleText: {
    color: "#2563EB",
    fontWeight: "500",
    fontSize: 13,
  },
  exampleContainer: {
    marginTop: 12,
    padding: 16,
    backgroundColor: "#EFF6FF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#BFDBFE",
  },
  exampleHeading: {
    fontSize: 13,
    fontWeight: "600",
    color: "#1D4ED8",
  },
  exampleBody: {
    marginTop: 8,
    fontSize: 13,
    lineHeight: 18,
    color: "#1F2937",
  },
  exampleList: {
    marginTop: 8,
    gap: 4,
  },
  exampleListItem: {
    fontSize: 13,
    color: "#1F2937",
  },
  exampleFootnote: {
    marginTop: 12,
    fontSize: 12,
    color: "#4B5563",
  },
});

export default ConsultSettings;
